import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import useMediaQuery from "@nishtech/Utils/NishtechMediaQuery";
import * as React from "react";
import styled, { withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

export const StyledSection = styled.section.attrs({ "aria-label": "breadcrumbs" })`
    ${p => p.theme?.breadcrumbs?.defaultProps?.css || ""}

    a {
        background-color: transparent;

        text-decoration: ${p =>
            p.theme?.breadcrumbs?.defaultProps?.typographyProps?.underline ? "underline" : "none"};

        &:hover {
            color: ${p =>
                p.theme?.breadcrumbs?.defaultProps?.typographyProps?.hoverColor ||
                p.theme?.breadcrumbs?.defaultProps?.hoverColor ||
                p.theme?.colors?.secondary?.main ||
                "#c8102e"};
        }
    }

    * {
        box-sizing: inherit;
    }
    &:after,
    &:before {
        box-sizing: inherit;
    }
    ul {
        margin: 0 0 22px;
    }

    &.as-breadcrumbs-section {
        .as-container {
            max-width: 1310px;
            margin: auto;
        }
    }

    .as-breadcrumb-list {
        display: flex;
        flex-wrap: wrap;
        font-size: ${p => {
            const size = p.theme?.breadcrumbs?.defaultProps?.typographyProps?.size;
            const fontSize = parseFloat(size) || 14;
            return `${fontSize}px`;
        }};
        letter-spacing: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.letterSpacing || ".22px"};
        list-style: none;
        margin: 0;
        padding: 0;
        text-transform: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.transform || "capitalize"};
        font-weight: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.weight || 500};
        line-height: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.lineHeight || "1.4"};
        font-family: ${p =>
            p.theme?.breadcrumbs?.defaultProps?.typographyProps?.fontFamily || `"Inter Tight", Arial, sans-serif`};
        font-style: ${p => (p.theme?.breadcrumbs?.defaultProps?.typographyProps?.italic ? "italic" : "normal")};

        li {
            font-size: inherit;

            & + li {
                &::before {
                    content: "/";
                    margin: 0 5px;
                }
            }
        }

        a {
            font-size: inherit;
            text-decoration: ${p =>
                p.theme?.breadcrumbs?.defaultProps?.typographyProps?.underline ? "underline" : "none"};
            color: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.color || p.theme?.colors?.text?.main};
            transition: color 0.3s ease;

            &:hover {
                color: ${p =>
                    p.theme?.cms?.breadcrumbs?.hoverColor ||
                    p.theme?.breadcrumbs?.defaultProps?.typographyProps?.hoverColor ||
                    p.theme?.breadcrumbs?.defaultProps?.hoverColor ||
                    p.theme?.colors?.secondary?.main};
            }
        }
    }

    @media (min-width: 768px) {
        &.as-breadcrumbs-section {
            padding: 51px 0 20px;
        }

        .as-breadcrumb-list {
            font-size: ${p => {
                const size = p.theme?.breadcrumbs?.defaultProps?.typographyProps?.size;
                const fontSize = parseFloat(size) || 14;
                return `${fontSize}px`;
            }};
            letter-spacing: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.letterSpacing || ".28px"};
            font-family: ${p =>
                p.theme?.breadcrumbs?.defaultProps?.typographyProps?.fontFamily || `"Inter Tight", Arial, sans-serif`};
            font-style: ${p => (p.theme?.breadcrumbs?.defaultProps?.typographyProps?.italic ? "italic" : "normal")};
        }
    }

    @media (max-width: 767px) {
        .as-breadcrumb-list {
            font-size: ${p => {
                const size = p.theme?.breadcrumbs?.defaultProps?.typographyProps?.size;
                const fontSize = parseFloat(size) || 14;
                return `${fontSize}px`;
            }};
            letter-spacing: ${p => p.theme?.breadcrumbs?.defaultProps?.typographyProps?.letterSpacing || ".22px"};
            font-family: ${p =>
                p.theme?.breadcrumbs?.defaultProps?.typographyProps?.fontFamily || `"Inter Tight", Arial, sans-serif`};
            font-style: ${p => (p.theme?.breadcrumbs?.defaultProps?.typographyProps?.italic ? "italic" : "normal")};

            li + li {
                &::before {
                    content: "/";
                    margin: 0 5px;
                }

                font-size: ${p => {
                    const size = p.theme?.breadcrumbs?.defaultProps?.typographyProps?.size;
                    const fontSize = parseFloat(size) || 14;
                    return `${fontSize}px`;
                }};
            }
        }
    }
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? `${children}_${index}` : index;
        if (index === links.length - 1) {
            return (
                <li aria-current="page" key={key} data-test-selector={`breadCrumb_${children}`}>
                    <Typography {...typographyProps}>{children}</Typography>
                </li>
            );
        }
        return (
            <li key={key}>
                <Link typographyProps={typographyProps} color={typographyProps.color} {...link} />
            </li>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    const isMobile = useMediaQuery("(max-width: 767px)");
    const validLinks = links.filter((link: any) => React.Children.count(link.children));

    return (
        <>
            <StyledSection className="as-breadcrumbs-section">
                <div className="as-container">
                    <nav aria-label="breadcrumb">
                        {isMobile ? (
                            <ul className="as-breadcrumb-list">
                                {(() => {
                                    const prevLink = validLinks[validLinks.length - 2];
                                    if (!prevLink) return null;
                                    return (
                                        <li>
                                            <Link
                                                typographyProps={typographyProps}
                                                color={typographyProps.color}
                                                {...prevLink}
                                            >
                                                &lsaquo; {prevLink.children}
                                            </Link>
                                        </li>
                                    );
                                })()}
                            </ul>
                        ) : (
                            <ul className="as-breadcrumb-list">{children}</ul>
                        )}
                    </nav>
                </div>
            </StyledSection>
        </>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
